import { useState } from "react";
import { ChangePasswordDialog } from "../ChangePasswordDialog";
import { KeyRound, LayoutDashboard, Users, Building2, Briefcase, FileText, ShieldCheck, AlertTriangle, BarChart2, DollarSign, Settings, LogOut, Tag, PanelLeftClose, ClipboardList, UserCheck, Megaphone, Percent } from "lucide-react";
import { CORAL, F, T } from "../../theme/tokens";
import logoWhite from "../../assets/Logo_White.svg";
import markWhite from "../../assets/Mark_White.svg";
import { getInitials } from "../../utils/getInitials";

// ── Sidebar nav ───────────────────────────────────────────────────────────────

export type NavItem = { id: string; label: string; icon: React.ReactNode; badge?: number };

export function getNavGroups(verificationBadge: number, moderationBadge: number): { label: string; items: NavItem[] }[] {
  return [
  {
    label: "Overview",
    items: [
      { id: "dashboard", label: "Dashboard", icon: <LayoutDashboard size={15} /> },
    ],
  },
  {
    label: "Platform",
    items: [
      { id: "category", label: "Category", icon: <Tag size={15} /> },
      { id: "postings", label: "Postings", icon: <Briefcase size={15} /> },
      { id: "applications", label: "Applications", icon: <FileText size={15} /> },
    ],
  },
  {
    label: "Users",
    items: [
      { id: "talents", label: "Talents", icon: <Users size={15} /> },
      { id: "orgs", label: "Organisations", icon: <Building2 size={15} /> },
    ],
  },
  {
    label: "Finance",
    items: [
      { id: "fees", label: "Fees", icon: <Percent size={15} /> },
      { id: "payments", label: "Payments", icon: <DollarSign size={15} /> },
      { id: "analytics", label: "Analytics", icon: <BarChart2 size={15} /> },
    ],
  },
  {
    label: "Operations",
    items: [
      { id: "verification", label: "Verification", icon: <ShieldCheck size={15} />, badge: verificationBadge },
      { id: "moderation", label: "Moderation", icon: <AlertTriangle size={15} />, badge: moderationBadge },
      { id: "communications", label: "Communications", icon: <Megaphone size={15} /> },
    ],
  },
  {
    label: "System",
    items: [
      { id: "admins", label: "Admin Users", icon: <UserCheck size={15} /> },
      { id: "audit", label: "Audit Log", icon: <ClipboardList size={15} /> },
      { id: "settings", label: "Settings", icon: <Settings size={15} /> },
    ],
  },
  ];
}

export function Sidebar({ active, onChange, collapsed: collapsedProp, onToggle, onLogout, admin, navGroups, mobile = false }: {
  mobile?: boolean;
  active: string; onChange: (id: string) => void; collapsed: boolean;
  onToggle: () => void; onLogout: () => void;
  admin?: { name: string; email: string; role: string };
  navGroups: { label: string; items: NavItem[] }[];
}) {
  const [passwordOpen, setPasswordOpen] = useState(false);
  // In the mobile drawer the rail never collapses; the drawer itself is the toggle.
  const collapsed = mobile ? false : collapsedProp;
  return (
    <aside style={mobile
      ? { width: "100%", background: T.sidebar, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }
      : { width: collapsed ? 56 : 220, background: T.sidebar, display: "flex", flexDirection: "column", height: "100vh", position: "sticky", top: 0, flexShrink: 0, transition: "width 0.2s ease", overflow: "hidden" }}>
      {/* Logo */}
      <div style={{ padding: collapsed ? "14px 0" : "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start" }}>
        {!collapsed ? (
          <img src={logoWhite} alt="Donatella" height={18} width={Math.round((840 / 117) * 18)} style={{ display: "block" }} draggable={false} />
        ) : (
          <img src={markWhite} alt="Donatella" height={22} width={Math.round((101 / 114) * 22)} style={{ display: "block" }} draggable={false} />
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: "auto", padding: "10px 0", scrollbarWidth: "none" }}>
        {navGroups.map((group) => (
          <div key={group.label} style={{ marginBottom: "4px" }}>
            {!collapsed && (
              <p style={{ fontSize: "9px", fontWeight: 600, letterSpacing: "0.1em", color: T.sidebarSub, fontFamily: F, textTransform: "uppercase", padding: "10px 18px 4px" }}>{group.label}</p>
            )}
            {group.items.map((item) => {
              const on = active === item.id;
              return (
                <button key={item.id} onClick={() => onChange(item.id)}
                  title={collapsed ? item.label : undefined}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: collapsed ? "9px 0" : "8px 18px", justifyContent: collapsed ? "center" : "flex-start", background: on ? T.sidebarActive : "transparent", borderLeft: on ? `2px solid ${T.sidebarActiveBorder}` : "2px solid transparent", color: on ? "#fff" : T.sidebarText, cursor: "pointer", border: "none", borderRadius: 0, transition: "background 0.15s", fontFamily: F, fontSize: "12px", fontWeight: on ? 600 : 400, position: "relative", opacity: on ? 1 : 0.8 }}>
                  <span style={{ flexShrink: 0 }}>{item.icon}</span>
                  {!collapsed && <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>}
                  {!collapsed && item.badge ? <span style={{ background: CORAL, color: "#fff", borderRadius: "9999px", fontSize: "10px", fontWeight: 700, padding: "1px 7px", lineHeight: "16px" }}>{item.badge}</span> : null}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: collapsed ? "12px 0" : "12px 14px", display: "flex", flexDirection: "column", gap: "6px" }}>
        {!collapsed && (
          <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 4px" }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: CORAL, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: "#fff", fontFamily: F }}>{admin ? getInitials(admin.name) : "SA"}</span>
            </div>
            <div>
              <p style={{ fontSize: "11px", fontWeight: 600, color: "#e0e0e0", fontFamily: F }}>{admin?.name ?? "Super Admin"}</p>
              <p style={{ fontSize: "10px", color: T.sidebarSub, fontFamily: F }}>{admin?.email ?? "admin@donatella.sa"}</p>
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: "4px", justifyContent: collapsed ? "center" : "flex-start" }}>
          <button onClick={() => setPasswordOpen(true)} title="Change password" style={{ display: "flex", alignItems: "center", gap: "5px", background: "none", border: "none", color: T.sidebarSub, cursor: "pointer", padding: "5px", borderRadius: "6px", fontSize: "11px", fontFamily: F }}>
            <KeyRound size={13} />
            {!collapsed && "Password"}
          </button>
          <button onClick={onLogout} title="Log out" style={{ display: "flex", alignItems: "center", gap: "5px", background: "none", border: "none", color: T.sidebarSub, cursor: "pointer", padding: "5px", borderRadius: "6px", fontSize: "11px", fontFamily: F }}>
            <LogOut size={13} />
            {!collapsed && "Log out"}
          </button>
          {!mobile && (
          <button onClick={onToggle} title={collapsed ? "Expand" : "Collapse"} style={{ display: "flex", alignItems: "center", gap: "5px", background: "none", border: "none", color: T.sidebarSub, cursor: "pointer", padding: "5px", borderRadius: "6px", marginLeft: "auto" }}>
            <PanelLeftClose size={13} style={{ transform: collapsed ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </button>
          )}
        </div>
      </div>
      {passwordOpen && <ChangePasswordDialog onClose={() => setPasswordOpen(false)} />}
    </aside>
  );
}
